import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { BP_MOBILE_MIN, BP_TABLET_MIN } from '../../assets/styles/breakpoints';
import dlt from '../../utils/default-lang';
import useWindowSize from '../../utils/screen-size';
import { formatDashboardNumber } from '../../utils/helpers';
import { BodyText, HeadingTwo, SmallText } from '../typography';
import Button from '../ui/button';
import InvestorDashBannerSC, {
  ChartsWrapSC,
  InvestChartWrapSC,
  InvestCircleChartWrapSC,
} from './investor-dash-banner.styled';
//import SmallChart from '../charts/small-chart';
import numeral from 'numeral';
import InvestChart from '../charts/invest-chart';
import InvestCircleChart from '../charts/invest-circle-chart';
import InlineLoading from '../ui/inline-loading/inline-loading';
import DashBannerDesktop from './components/dash-banner-desktop';
import DashBannerTablet from './components/dash-banner-tablet';

const getValueIfPositive = (number) =>
  Number(number) > 0 ? `+${formatDashboardNumber(number)} %` : '-';

const _InvestorDashBanner = ({ withInvChart, dashboardData, history }) => {
  const screenSize = useWindowSize();
  const isTablet = screenSize.width < BP_TABLET_MIN;
  const isMobile = screenSize.width < BP_MOBILE_MIN;
  const [reinvest, setReinvest] = useState(undefined);
  const [circleChartData, setCircleChartData] = useState(null);
  const [circleChartTotals, setCircleChartTotals] = useState(null);
  const [circleChartStats, setCircleChartStats] = useState(null);
  const [graphMode, setGraphMode] = useState('count');
  const [evaluationToggle, setEvaluationToggle] = useState('first');
  const [reinvestToggle, setReinvestToggle] = useState('first');

  const handleEvaluationToggle = useCallback((evaluation) => {
    setEvaluationToggle(evaluation);
  }, []);
  const handleReinvestToggle = useCallback((reinvest) => {
    setReinvestToggle(reinvest);
  }, []);

  useEffect(() => {
    if (dashboardData.loans) {
      let newDataForCircleChart = [
        { value: dashboardData.loans['paid'].count, name: 'paid', selected: false },
        { value: dashboardData.loans['medium-delay'].count, name: 'medium-delay', selected: false },
        { value: dashboardData.loans['high-delay'].count, name: 'high-delay', selected: false },
      ];
      let newDataForCircleChartTotals = {
        count: dashboardData.loans.total.count,
        total_amount: dashboardData.loans.total.total,
        delayed_amount: dashboardData.loans.total.delayed,
      };

      let newDataForCircleChartStats;
      if (graphMode === 'count') {
        newDataForCircleChartStats = [
          { ...dashboardData.loans['paid'], name: 'paid' },
          { ...dashboardData.loans['medium-delay'], name: 'medium-delay' },
          { ...dashboardData.loans['high-delay'], name: 'high-delay' },
        ];
      } else {
        const sum =
          dashboardData.loans['paid'].count +
          dashboardData.loans['medium-delay'].count +
          dashboardData.loans['high-delay'].count;

        newDataForCircleChartStats = [
          {
            ...dashboardData.loans['paid'],
            name: 'paid',
            count: ((dashboardData.loans['paid'].count / sum) * 100).toFixed(2),
          },
          {
            ...dashboardData.loans['medium-delay'],
            name: 'medium-delay',
            count: ((dashboardData.loans['medium-delay'].count / sum) * 100).toFixed(2),
          },
          {
            ...dashboardData.loans['high-delay'],
            name: 'high-delay',
            count: ((dashboardData.loans['high-delay'].count / sum) * 100).toFixed(2),
          },
        ];
      }

      setCircleChartTotals(newDataForCircleChartTotals);
      setCircleChartData(newDataForCircleChart);
      setCircleChartStats(newDataForCircleChartStats);
    }
  }, [dashboardData, graphMode]);

  const redirectToInvestDeposit = () => {
    history.push('/invest-manage/deposit');
  };

  const redirectToInvestChange = () => {
    history.push('/invest-manage/change');
  };

  useEffect(() => {
    if (Object.keys(dashboardData).length !== 0) {
      if (dashboardData.reinvestRate && (dashboardData.reinvestRate * 100).toFixed(0) > 99) {
        setReinvest('fully-reinvested');
      } else if (dashboardData.reinvestRate && (dashboardData.reinvestRate * 100).toFixed(0) > 0) {
        setReinvest('reinvesting');
      } else {
        setReinvest('not-reinvested');
      }
    } else {
    }
  }, [dashboardData]);

  if (Object.keys(dashboardData).length === 0) {
    return (
      <InvestorDashBannerSC>
        <div className="loading-wrap">
          <InlineLoading />
        </div>
      </InvestorDashBannerSC>
    );
  }

  if (!dashboardData) {
    return null;
  }
  const evaluationValue1 = formatDashboardNumber(
    Number(dashboardData.totalTillEnd[5]) + Number(dashboardData.repayment[5])
  );
  const evaluationValue2 = formatDashboardNumber(dashboardData.totalTillEnd[2]);
  const evaluationPercentage1 = getValueIfPositive(dashboardData.percentages.revenue);
  const evaluationPercentage2 = getValueIfPositive(dashboardData.percentages.revenue_expected);
  const reinvestValue1 = formatDashboardNumber(dashboardData.used.credits);
  const reinvestValue2 = formatDashboardNumber(dashboardData.used.investments);
  const reinvestPercentage = `${formatDashboardNumber(dashboardData.percentages.used_credits)} %`;

  return (
    <>
      <InvestorDashBannerSC withInvChart={withInvChart}>
        <div className="left-wrap">
          <BodyText className="grey bolder">
            <FormattedMessage
              id="investor_dash_banner_invest_amount"
              defaultMessage={dlt['investor_dash_banner_invest_amount']}
            />
          </BodyText>
          <HeadingTwo className="white left-wrap--main-headline">
            {numeral(Number(dashboardData.netCredits)).format('0,00.00')} €
          </HeadingTwo>
          <SmallText className="grey">
            <FormattedMessage
              id="investor_dash_banner_max_loan_amount"
              defaultMessage={dlt['investor_dash_banner_max_loan_amount']}
            />{' '}
            {numeral(Number(dashboardData.minInvestment)).format('0,00.00')} -{' '}
            {numeral(Number(dashboardData.maxInvestment)).format('0,00.00')} €
          </SmallText>
          <Button type="PRIMARY" size="MEDIUM" onClick={() => redirectToInvestDeposit()}>
            <FormattedMessage
              id="investor_dash_banner_new_instruction"
              defaultMessage={dlt['investor_dash_banner_new_instruction']}
            />
          </Button>
        </div>
        <div className="right-wrap">
          {isTablet ? (
            <DashBannerTablet
              dashboardData={dashboardData}
              handleEvaluationToggle={handleEvaluationToggle}
              handleReinvestToggle={handleReinvestToggle}
              evaluationToggle={evaluationToggle}
              reinvestToggle={reinvestToggle}
              reinvest={reinvest}
              redirectToInvestChange={redirectToInvestChange}
              evaluationValue1={evaluationValue1}
              evaluationValue2={evaluationValue2}
              reinvestValue1={reinvestValue1}
              reinvestValue2={reinvestValue2}
              evaluationPercentage1={evaluationPercentage1}
              evaluationPercentage2={evaluationPercentage2}
              reinvestPercentage={reinvestPercentage}
            />
          ) : (
            <DashBannerDesktop
              dashboardData={dashboardData}
              handleEvaluationToggle={handleEvaluationToggle}
              evaluationToggle={evaluationToggle}
              reinvestToggle={reinvestToggle}
              handleReinvestToggle={handleReinvestToggle}
              reinvest={reinvest}
              redirectToInvestChange={redirectToInvestChange}
              evaluationValue1={evaluationValue1}
              evaluationValue2={evaluationValue2}
              reinvestValue1={reinvestValue1}
              reinvestValue2={reinvestValue2}
              evaluationPercentage1={evaluationPercentage1}
              evaluationPercentage2={evaluationPercentage2}
              reinvestPercentage={reinvestPercentage}
            />
          )}
        </div>
      </InvestorDashBannerSC>

      {withInvChart ? (
        <ChartsWrapSC>
          <InvestChartWrapSC>
            <InvestChart
              chartData={
                dashboardData.revenuesGraph && dashboardData.revenuesGraph.length > 0
                  ? dashboardData.revenuesGraph
                  : null
              }
            />
          </InvestChartWrapSC>
          <InvestCircleChartWrapSC>
            <InvestCircleChart
              isMobile={isMobile}
              chartData={circleChartData}
              totals={circleChartTotals}
              stats={circleChartStats}
              graphMode={graphMode}
              setMode={setGraphMode}
            />
          </InvestCircleChartWrapSC>
        </ChartsWrapSC>
      ) : null}
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    dashboardData: state.dashboard,
  };
};

const InvestorDashBanner = withRouter(connect(mapStateToProps)(_InvestorDashBanner));

export default InvestorDashBanner;
